import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Decision, Lifecycle, Confidence } from '@mas-flow/types';
import { type FilterState, EMPTY_FILTERS, type GroupBy } from '../lib/filters/types';

const DECISION_VALUES = new Set<Decision>(['Now', 'Next', 'Later', 'Parked']);
const LIFECYCLE_VALUES = new Set<Lifecycle>(['Submitted', 'InTriage', 'Scored', 'Decided', 'Done', 'Archived']);
const CONFIDENCE_VALUES = new Set<Confidence>(['High', 'Medium', 'Low']);
const GROUP_VALUES = new Set<GroupBy>(['decision', 'lifecycle', 'priorityBand', 'category']);

function csv(s: string | null): string[] {
  return s ? s.split(',').filter(Boolean) : [];
}
function boolish(s: string | null): boolean {
  return s === '1';
}

function parseDecision(csvVal: string[]): (Decision | null)[] {
  const out: (Decision | null)[] = [];
  for (const v of csvVal) {
    if (v === 'null') out.push(null);
    else if (DECISION_VALUES.has(v as Decision)) out.push(v as Decision);
  }
  return out;
}

function parse(searchParams: URLSearchParams): FilterState {
  return {
    q: searchParams.get('q') ?? '',
    category: csv(searchParams.get('category')),
    decision: parseDecision(csv(searchParams.get('decision'))),
    lifecycle: csv(searchParams.get('lifecycle')).filter((v): v is Lifecycle => LIFECYCLE_VALUES.has(v as Lifecycle)),
    confidence: csv(searchParams.get('confidence')).filter((v): v is Confidence => CONFIDENCE_VALUES.has(v as Confidence)),
    anchorsOnly: boolish(searchParams.get('anchorsOnly')),
    showArchived: boolish(searchParams.get('showArchived')),
    group: (() => {
      const g = searchParams.get('group');
      return g && GROUP_VALUES.has(g as GroupBy) ? (g as GroupBy) : null;
    })(),
    sort: csv(searchParams.get('sort')).flatMap((tok) => {
      const [field, dir] = tok.split(':');
      if (!field || (dir !== 'asc' && dir !== 'desc')) return [];
      return [{ field, dir }];
    }),
    cols: csv(searchParams.get('cols')),
  };
}

function serialize(prev: URLSearchParams, f: FilterState): URLSearchParams {
  const next = new URLSearchParams(prev);
  const set = (key: string, value: string | undefined) => {
    if (value && value.length > 0) next.set(key, value);
    else next.delete(key);
  };
  set('q', f.q || undefined);
  set('category', f.category.length ? f.category.join(',') : undefined);
  set('decision', f.decision.length ? f.decision.map((d) => d === null ? 'null' : d).join(',') : undefined);
  set('lifecycle', f.lifecycle.length ? f.lifecycle.join(',') : undefined);
  set('confidence', f.confidence.length ? f.confidence.join(',') : undefined);
  set('anchorsOnly', f.anchorsOnly ? '1' : undefined);
  set('showArchived', f.showArchived ? '1' : undefined);
  set('group', f.group ?? undefined);
  set('sort', f.sort.length ? f.sort.map((s) => `${s.field}:${s.dir}`).join(',') : undefined);
  set('cols', f.cols.length ? f.cols.join(',') : undefined);
  return next;
}

export function useUrlFilters(): { filters: FilterState; setFilters: (f: FilterState) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parse(searchParams), [searchParams]);
  const setFilters = useCallback(
    (f: FilterState) => {
      setSearchParams((prev) => serialize(prev, f));
    },
    [setSearchParams],
  );
  return { filters, setFilters };
}

export { EMPTY_FILTERS };
